import { AppConfiguration } from '@schemas/app.interface';
import { AppService } from '@services/app/app.service';
import { AuthService } from '@services/auth.service';
import { DialogService } from '@services/app/dialog.service';
import { MemberBio } from '@schemas/community/member-bio';
import { NgxSpinnerService } from 'ngx-spinner';
import { PlpFiltersMobileMenuComponent } from './plp-filters-mobile-menu/plp-filters-mobile-menu.component';
import { ProductService } from '@services/product.service';
import { ScreenDetectorBootstrapService } from '@services/app/screen-detector-bootstrap.service';
import { SearchService } from '@services/search/search.service';
import { catchError } from 'rxjs/operators';
import { ActivatedRoute, Data, Params, Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Hit, SearchResponse } from '../../components/search/search-response.interface';
import { Filters, PaginationConfig, SearchRequest, Sorting } from '@schemas/search/search-request';
import {
  NewArrivalTimestampsInSeconds,
  getNewInTimestampsInSeconds,
} from '../../../default/pages/home-view/home-view.component';
import GenericUtils from '@shared/utils/generic-utils';
import { SearchOperatorsEnum } from '@shared/enums/search-api.enum';
import SearchHelper from '@shared/helpers/search-helper';
import { FiltersSectionComponent } from '../../components/search/filters-section/filters-section.component';
import { UserService } from '@services/user.service';
import { CookieService } from 'ngx-cookie-service';
import { APP_CONSTANTS } from '@shared/constants/app-constants';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';

const CATEGORY_FILTER_NAME = 'category';
const SUBCATEGORY_FILTER_NAME = 'sub';
const COLOR_FILTER_NAME = 'color';
const PRICE_FILTER_NAME = 'price';
const BRAND_FILTER_NAME = 'brand';
const STYLE_FILTER_NAME = 'style';
const CONDITION_FILTER_NAME = 'condition';
const DISCOUNT_FILTER_NAME = 'discount';
const SHOES_SIZE_FILTER_NAME = 'shoes';
const CLOTHING_SIZE_FILTER_NAME = 'clothing';
const MEMBER_FILTER_NAME = 'user';
const USER_ID_FILTER_NAME = 'user_id.keyword';

@Component({
  selector: 'tvb-listing-view-elk',
  templateUrl: './listing-view-elk.component.html',
  styleUrls: ['./listing-view-elk.component.scss'],
})
export class ListingViewElkComponent implements OnInit {
  @ViewChild(PlpFiltersMobileMenuComponent) mobileMenuComponent: PlpFiltersMobileMenuComponent;
  @ViewChild(FiltersSectionComponent) filtersSectionComponent: FiltersSectionComponent;
  @Input() member: MemberBio = null;
  @Input() filter?: Filters = null;
  @Input() showFilter: boolean = true;
  @Input() numOfNotLazyProdImages?: number = 0;
  @Output() outputCall: EventEmitter<unknown> = new EventEmitter();
  @Output() pageNotFound: EventEmitter<boolean> = new EventEmitter();
  public isPageNotFound: boolean = false;
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public searchRequest: SearchRequest;
  public appConfig: AppConfiguration;
  public products: Hit[] = [];
  public paginationConfig: PaginationConfig;
  public sortByOptions: Array<SelectOption> = [];
  public selectedSortBy: SelectOption;
  public saleBadgePercentageActive: boolean = false;
  public isAnyFilterSelected: boolean = false;
  public isMemberFiltersExpanded: boolean = true;
  private routeDataParams: Data;
  private routePathParams: Params;
  private routeQueryParams: Params;
  private language: string;
  private currency: string;
  private conditionsDictionary: { [key: number]: string };

  constructor(
    public searchService: SearchService,
    public appService: AppService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private screenSizeDetector: ScreenDetectorBootstrapService,
    public authService: AuthService,
    public userService: UserService,
    public productService: ProductService,
    private dialogService: DialogService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.translateService.get(['']).subscribe(translations => {
      this.sortByOptions = [
        { label: this.translateService.instant('Sort by Recommended'), value: 'recommended' },
        { label: this.translateService.instant('Sort by New In'), value: 'new_in' },
        { label: this.translateService.instant('Sort by High to Low'), value: 'price_desc' },
        { label: this.translateService.instant('Sort by Low to High'), value: 'price_asc' },
        { label: this.translateService.instant('Sort by Most Wanted'), value: 'total_wishlisted' },
      ];

      this.conditionsDictionary = {
        1: this.translateService.instant('New with tags'),
        2: this.translateService.instant('Excellent'),
        4: this.translateService.instant('Good but used'),
        5: this.translateService.instant('Worn with love'),
      };
    });

    this.searchRequest = {
      page: 0,
      pageSize: 60,
      filters: [],
      sorting: null,
      currency: '',
      language: '',
    };
    this.paginationConfig = {
      currentPage: this.searchRequest.page,
      itemsPerPage: this.searchRequest.pageSize,
      totalItems: 600,
    };

    if (this.searchService.products.value) this.products = this.searchService.products.value ?? [];
  }

  async ngOnInit(): Promise<void> {
    this.isMobile$ = this.screenSizeDetector.isMobile;
    this.appService.getAppConfigurationBehavior().subscribe((val: AppConfiguration) => {
      this.appConfig = val;
      this.language = this.appConfig?.languageShortName || 'en';
      this.currency = this.appConfig?.currencyCode || 'eur';
    });
    this.routeDataParams = this.activatedRoute.snapshot.data;
    this.routePathParams = this.activatedRoute.snapshot.params;
    this.routeQueryParams = this.activatedRoute.snapshot.queryParams;
    this.searchRequest.currency = this.currency;
    this.searchRequest.language = this.language || this.routeDataParams['language'];
    this.searchService.searchRequest.next(this.searchRequest);

    this.setupTVBDestinationFilter();

    if (this.routePathParams?.parent_category_seo_url === 'searchitems') {
      this.setupFilterAccordingToUrlPathParams({
        pathParamName: 'child_category_seo_url',
        filterFieldName: 'searchItems',
        plpFor: 'searchItems',
      });
    }
    else {
      this.setupFilterAccordingToUrlPathParams({
        pathParamName: 'search_query',
        filterFieldName: 'searchItems',
        plpFor: 'searchItems',
      });
      this.setupFilterAccordingToUrlPathParams({
        pathParamName: 'parent_category_seo_url',
        filterFieldName: CATEGORY_FILTER_NAME,
      });
      this.setupFilterAccordingToUrlPathParams({
        pathParamName: 'child_category_seo_url',
        filterFieldName: SUBCATEGORY_FILTER_NAME,
      });
    }

    this.setupFilterAccordingToUrlPathParams({
      pathParamName: 'designer_seo_url',
      filterFieldName: 'brand',
      plpFor: 'designer',
    });
    this.setupFilterAccordingToUrlPathParams({
      pathParamName: 'styles_seo_url',
      filterFieldName: 'style',
      plpFor: 'style',
    });

    this.setupFilterAccordingToUrlPathParams({
      pathParamName: 'discover_seo_url',
      filterFieldName: 'discover',
      plpFor: 'discover',
    });

    this.setupFilterAccordingToUrlQueryParams();
    this.setupPaginationInSearchFilter(this.routeQueryParams['page']);
    this.setupFilterAccordingToSale();
    this.setupFilterForNewInPage();
    this.initSortBy();
    // this.setupSortingForWeLovePage(); // ** Disable we love **
    this.doSearch();
    this.getSaleBadgePercentageActive();
  }

  private setupFiltersExpandedForMembers() {
    if (this.member && this.products.length < 10) this.isMemberFiltersExpanded = false;
  }

  private getSaleBadgePercentageActive() {
    this.appService.getSaleBadgePercentageEnable().subscribe(active => {
      this.saleBadgePercentageActive = active;
    });
  }

  private setupFilterAccordingToUrlQueryParams(): void {
    // Checking if the URL query path has several params
    if (Object.keys(this.routeQueryParams).length > 0) {
      this.handlePathParamRepeatedInQueryParams('parent_category_seo_url', CATEGORY_FILTER_NAME);
      // this.handlePathParamRepeatedInQueryParams('child_category_seo_url', SUBCATEGORY_FILTER_NAME);
      this.setupQueryParamForDependentPathParam({
        pathParamName: 'search_query',
        dependentQueryParam: CATEGORY_FILTER_NAME,
        couldPathParamBeRepeatedInQueryParams: false,
      });

      this.setupSearchFilterForField(SUBCATEGORY_FILTER_NAME);
      this.setupSearchFilterForField(COLOR_FILTER_NAME);
      this.setupSearchFilterForField(PRICE_FILTER_NAME);
      this.setupSearchFilterForField(BRAND_FILTER_NAME);
      this.setupSearchFilterForField(STYLE_FILTER_NAME);
      this.setupSearchFilterForField(CONDITION_FILTER_NAME);
      this.setupSearchFilterForField(DISCOUNT_FILTER_NAME);
      this.setupSearchFilterForField(SHOES_SIZE_FILTER_NAME);
      this.setupSearchFilterForField(CLOTHING_SIZE_FILTER_NAME);
    }

    if (this.member)
      this.setupSearchFilterForField(MEMBER_FILTER_NAME, SearchOperatorsEnum.EQUALS, this.member.user_id);

    if (this.authService.isLoggedIn$.value && !this.member) {
      this.userService.getUserData().subscribe(user => {
        this.setupSearchFilterForField(USER_ID_FILTER_NAME, SearchOperatorsEnum.NUMERIC_EQUAL, user.id);
      });
    }
    else {
      const anonymous_user = this.cookieService.get(APP_CONSTANTS.COOKIE_KEYS.ANONYMOUS_ID);

      this.setupSearchFilterForField(USER_ID_FILTER_NAME, SearchOperatorsEnum.EQUALS, anonymous_user);
    }

    if (this.filter) this.setupSearchFilterForField(this.filter.field, this.filter.operator, this.filter.value);
  }

  private setupFilterForNewInPage() {
    if (this.routeDataParams.plpFor == 'new-in') {
      const { from, to } = getNewInTimestampsInSeconds(0, 336);

      this.searchRequest.filters.push(
        {
          field: 'from',
          value: from.toString(),
          operator: SearchOperatorsEnum.GREATER_THAN_OR_EQUAL,
        },
        {
          field: 'to',
          value: to.toString(),
          operator: SearchOperatorsEnum.LESS_THAN_OR_EQUAL,
        }
      );
      this.searchService.searchRequest.next(this.searchRequest);
    }
  }

  // ** Disable we love **
  // private setupSortingForWeLovePage() {
  //   if (this.routeDataParams.plpFor === 'discover' && this.routePathParams.discover_seo_url === 'we-love') {
  //     const sorting: Sorting[] = [{ field: 'approved_at', order: 'desc' }];

  //     this.searchRequest = { ...this.searchRequest, sorting };
  //     this.searchService.searchRequest.next({ ...this.searchRequest, sorting });
  //     this.searchService.sorting.next(sorting);
  //     this.setSorting(sorting);
  //   }
  // }

  private handlePathParamRepeatedInQueryParams(pathParamName: string, queryParamName: string): void {
    if (pathParamName in this.routePathParams && Array.isArray(this.routeQueryParams[queryParamName])) {
      this.searchRequest.filters = this.searchRequest.filters.filter(_ => !(_.field === queryParamName));
      this.searchService.searchRequest.next(this.searchRequest);
      this.setupSearchFilterForField(queryParamName);
    }
  }

  private setupQueryParamForDependentPathParam(options: {
    pathParamName: string;
    dependentQueryParam: string;
    couldPathParamBeRepeatedInQueryParams: boolean;
  }): void {
    if (options.pathParamName in this.routePathParams) {
      this.setupSearchFilterForField(options.dependentQueryParam);
    }

    if (options.couldPathParamBeRepeatedInQueryParams) {
      this.handlePathParamRepeatedInQueryParams(options.pathParamName, options.dependentQueryParam);
    }
  }

  private setupTVBDestinationFilter() {
    this.searchRequest.filters.push({
      field: 'destinations',
      operator: SearchOperatorsEnum.EQUALS,
      value: 'tvb',
    });
  }

  /**
   * @param options.pathParamName: the name of parameter found in the URL path (activatedRoute.snapshot.params)
   * @param options.filterFieldName: the name of field as the SearchAPI expects
   * @param options.plpFor: the value of the 'plpFor' property that comes in the activatedRoute.snapshot.data section
   * @param options.operator: the type of the operator for the search
   */
  private setupFilterAccordingToUrlPathParams(options: {
    pathParamName: string;
    filterFieldName: string;
    operator?: string;
    plpFor?: string;
  }): void {
    options.operator ??= '==';
    options.plpFor ??= '';

    if (options.pathParamName in this.routePathParams) {
      this.searchRequest.filters.push({
        field: options.filterFieldName,
        value: this.routePathParams[options.pathParamName],
        operator: options.operator,
      });

      this.searchService.searchRequest.next(this.searchRequest);
    }
  }

  public setupFilterAccordingToSale() {
    if (this.routeDataParams.plpFor == 'sale') {
      this.searchRequest.filters.push(
        {
          field: 'is_discount',
          value: '1',
          operator: SearchOperatorsEnum.NUMERIC_EQUAL,
        },
        {
          field: 'discount_percentage',
          value: '3',
          operator: SearchOperatorsEnum.GREATER_THAN_OR_EQUAL,
        }
      );
      this.searchService.searchRequest.next(this.searchRequest);
    }
  }

  /**
   * Given a fieldName and operator, it will fetch the respective value(s) and will aggregate the Search Filter.
   *
   * @param fieldName
   * @param operator
   * @param value
   *
   */
  private setupSearchFilterForField(fieldName: string, operator = '==', value = null): void {
    let fieldValues: string[] = [];

    // In case of only one param value
    if (typeof this.routeQueryParams[fieldName] == 'string') {
      fieldValues.push(this.routeQueryParams[fieldName]);
    }

    // In case of more than one param value
    if (Array.isArray(this.routeQueryParams[fieldName])) {
      fieldValues = [...this.routeQueryParams[fieldName]];
    }

    // In case of non query param value
    if (value || value === 0) fieldValues.push(value?.toString());

    fieldValues.forEach((fieldValue: string) => {
      this.searchRequest.filters.push({
        field: fieldName,
        value: fieldValue,
        operator: operator,
      });
      this.searchService.searchRequest.next(this.searchRequest);
    });
  }

  private setupPaginationInSearchFilter(page = 1): void {
    this.searchRequest = { ...this.searchRequest, page: page - 1 };
    this.searchService.searchRequest.next(this.searchRequest);
    this.paginationConfig.currentPage = page;
  }

  doSearch(): void {
    this.spinner.show();
    const isWeLove: boolean
      = this.searchRequest.filters.filter((filter: Filters) => filter.value === 'we-love').length > 0;

    if (isWeLove) {
      // Disable we love page, setTimeout is for change detection
      setTimeout(() => {
        this.products = [];
        this.searchService.products.next(this.products);
        this.paginationConfig.totalItems = 0;
        this.pageNotFound.emit(true);
        this.isPageNotFound = true;
        this.setupFiltersExpandedForMembers();
        this.loadSelectedFilters();
        this.spinner.hide();
      }, 0);
    }
    else {
      this.searchService
        .searchProduct(this.searchRequest)
        .pipe(
          catchError(err => {
            // eslint-disable-next-line no-console
            console.log(JSON.stringify(err));
            this.dialogService.openTvbDialog({
              title: 'Something went wrong',
              messages: [
                'An error has occurred while trying to fetch the data.',
                'Please try again later or contact us via contact@thevintagebar.com.',
                err,
              ],
              showActionButtons: false,
            });
            this.spinner.hide();

            return of({ total: 0, hits: [] });
          })
        )
        .subscribe({
          next: (response: SearchResponse) => {
            if (response) {
              this.products = response?.hits;
              this.searchService.products.next(this.products);
              this.paginationConfig.totalItems = response?.total;
              this.pageNotFound.emit(false);
              this.isPageNotFound = false;
            }
            else {
              this.products = [];
              this.searchService.products.next(this.products);
              this.paginationConfig.totalItems = 0;
              this.pageNotFound.emit(true);
              this.isPageNotFound = true;
            }

            this.setupFiltersExpandedForMembers();
            this.loadSelectedFilters();
            this.spinner.hide();
          },
        });
    }
  }

  onPageChange(nextPage: number, top: boolean = true): void {
    this.searchRequest.page = nextPage;
    this.paginationConfig.currentPage = nextPage;

    const pageQueryParam = window.location.search.match(/([?&]page=)(\d+)/);
    let destiny = window.location.pathname + window.location.search;
    let operatorToBeAdded = '?';

    if (pageQueryParam) {
      destiny = destiny.replace(pageQueryParam[0], '');

      if (GenericUtils.QueryParamsLength(window.location.search) > 1) {
        operatorToBeAdded = '&';
      }
    }

    if (!pageQueryParam && GenericUtils.QueryParamsLength(window.location.search) >= 1) {
      operatorToBeAdded = '&';
    }

    if (top)
      this.searchService.menuMobileOpen.next({ open: null, menu: null, top: this.doc.documentElement.scrollTop });

    if (!this.isMobile$.value) this.searchService.filters.next(this.filtersSectionComponent.sections);

    destiny += operatorToBeAdded + 'page=' + nextPage;
    this.router.navigateByUrl(decodeURI(destiny));
  }

  loadSelectedFilters() {
    this.isAnyFilterSelected = false;
    this.searchService.searchRequest.value.filters.forEach(item => {
      if (this.isTagVisible(item.field)) this.isAnyFilterSelected = true;
    });
  }

  removeTag(tag: any): void {
    if (!this.isMobile$.value) this.filtersSectionComponent.removeTag(tag);
    else this.mobileMenuComponent.removeTag(tag);
  }

  isTagVisible(filterName: string): boolean {
    switch (filterName) {
      case 'brand':
        return this.routeDataParams.plpFor !== 'designer';
      case 'style':
        return this.routeDataParams.plpFor !== 'style';
      case 'discover':
        return this.routeDataParams.plpFor !== 'discover';
      case 'is_discount':
      case 'discount_percentage':
        return this.routeDataParams.plpFor !== 'sale';
      case 'category':
        return this.routeDataParams.plpFor !== 'child-category';
      case 'from':
      case 'to':
      case 'user':
      case 'id':
      case 'user_id':
      case 'user_id.keyword':
      case 'searchItems':
      case 'destinations':
        return false;
      default:
        return true;
    }
  }

  prepareFilterValueForTag(filterName: string, filterValue: string): string {
    let result = '';

    switch (filterName) {
      case PRICE_FILTER_NAME: {
        const [minPrice, maxPrice]: string[] = filterValue.split('-');

        result += '≥ ' + this.appConfig?.currencySymbol + (!minPrice ? 0 : parseInt(minPrice));
        result += ' & ';
        result += '≤ ' + this.appConfig?.currencySymbol + (!maxPrice ? 99999 : parseInt(maxPrice));
        break;
      }
      case CONDITION_FILTER_NAME:
        return this.conditionsDictionary[filterValue];
      default:
        result = filterValue;
        break;
    }

    return result;
  }

  onClearFilters(closeMenu: boolean = false): void {
    this.searchRequest = {
      ...this.searchRequest,
      ...this.searchService.sorting.value,
      page: 0,
      pageSize: 60,
      filters: [],
    };
    this.doSearch();

    if (closeMenu) this.mobileMenuComponent.toggle();

    let urlPath = window.location.pathname;

    // remove the 'category' part from the url
    if (
      'parent_category_seo_url' in this.routePathParams
      && 'category' in this.routeQueryParams
      && this.routeDataParams.plpFor !== 'category'
    ) {
      let urlAux = urlPath.split('/');

      if (this.routeDataParams.plpFor !== 'child-category')
        urlAux = urlAux.filter(x => x !== this.routePathParams?.parent_category_seo_url);

      urlAux = urlAux.filter(x => x !== this.routePathParams?.child_category_seo_url);
      urlPath = urlAux.join('/');
    }

    this.router.navigateByUrl(urlPath);
  }

  toggleMobileMenu(): void {
    this.mobileMenuComponent.toggle();
  }

  onApplyMobileSearch(): void {
    this.doSearch();
    this.mobileMenuComponent.toggle();
  }

  onChangeSortBy(event: SelectOption): void {
    this.translateService.get(['']).subscribe(translations => {
      this.selectedSortBy = this.sortByOptions.find(_ => _.value == this.translateService.instant(event.value));
      const sorting: Sorting[] = [
        {
          field: null,
          order: null,
        },
      ];

      switch (event.value) {
        case this.translateService.instant('recommended'):
          sorting[0].field = 'recommend';
          sorting[0].order = 'desc';
          break;
        case this.translateService.instant('new_in'):
          sorting[0].field = 'approved_at';
          sorting[0].order = 'desc';
          break;
        case this.translateService.instant('price_asc'):
          sorting[0].field = 'discounted_price_' + this.appConfig.currencyCode;
          sorting[0].order = 'asc';
          break;
        case this.translateService.instant('price_desc'):
          sorting[0].field = 'discounted_price_' + this.appConfig.currencyCode;
          sorting[0].order = 'desc';
          break;
        case this.translateService.instant('total_wishlisted'):
          sorting[0].field = 'total_wishlisted';
          sorting[0].order = 'desc';
          break;
        default:
          break;
      }

      this.searchRequest = { ...this.searchRequest, sorting };
      this.searchService.searchRequest.next({ ...this.searchRequest, sorting });
      this.searchService.sorting.next(sorting);
      this.doSearch();
    });
  }

  private initSortBy(): void {
    const sorting = this.getSorting();

    this.setSorting(sorting);

    this.searchRequest = { ...this.searchRequest, sorting };
    this.searchService.searchRequest.next({ ...this.searchRequest, sorting });
  }

  getSorting() {
    if (this.searchService.sorting.value != null) return this.searchService.sorting.value;
    else {
      const sorting: Sorting[] = [
        {
          field: 'recommend',
          order: 'desc',
        },
      ];

      return sorting;
    }
  }

  setSorting(sorting: Sorting[]) {
    switch (sorting[0]?.field) {
      case 'recommend':
        this.selectedSortBy = this.sortByOptions[0];
        break;
      case 'approved_at':
        this.selectedSortBy = this.sortByOptions[1];
        break;
      case 'discounted_price_' + this.appConfig.currencyCode:
        this.selectedSortBy = sorting[0]?.order === 'asc' ? this.sortByOptions[3] : this.sortByOptions[2];
        break;
      case 'total_wishlisted':
        this.selectedSortBy = this.sortByOptions[4];
        break;
    }
  }
}

interface SelectOption {
  label: string;
  value: string;
}
